import { computed, ref } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { Profile, ProfileSectionKey } from '@/models/Profile';

export type AvatarPreview = {
  imageData: string;
  imageType: string;
  avatarPage?: number;
};

/**
 * AI-suggestion overlay for the profile. The extraction endpoint returns a
 * partial profile; sections present here are rendered "as if saved" via the
 * effectiveProfile computed in profileState.ts. The user resolves each
 * section individually (Annehmen / Editieren / Löschen) and the slot is
 * cleared when the decision is made.
 *
 * Lives client-side only — see architectural decision A in the plan doc.
 */

// Profile sections that the AI overlay can populate. The remaining
// ProfileSectionKey values (aggregate sections like certificatesEducations
// or company-only sections) don't have direct previews; they derive from
// other slots or aren't in scope for freelancer-PDF extraction.
type PreviewMap = {
  basic: 'basic';
  skills: 'skills';
  projectPreferences: 'projectPreferences';
  references: 'references';
  educations: 'educations';
  certificates: 'certificates';
  projects: 'experiences';
};

export type PreviewableSection = keyof PreviewMap;

const PREVIEWABLE_SECTION_TO_PROFILE_KEY: PreviewMap = {
  basic: 'basic',
  skills: 'skills',
  projectPreferences: 'projectPreferences',
  references: 'references',
  educations: 'educations',
  certificates: 'certificates',
  projects: 'experiences',
};

export const isPreviewableSection = (section: ProfileSectionKey): section is PreviewableSection => (
  section in PREVIEWABLE_SECTION_TO_PROFILE_KEY
);

export const profileKeyForSection = (section: PreviewableSection): keyof Profile => (
  PREVIEWABLE_SECTION_TO_PROFILE_KEY[section]
);

const useProfilePreview = createGlobalState(() => {
  const previewProfile = ref<Partial<Profile>>({});
  const avatarPreview = ref<AvatarPreview | null>(null);

  const hasUnresolvedFor = (section: ProfileSectionKey): boolean => {
    if (section === 'basic' && avatarPreview.value) return true;
    if (!isPreviewableSection(section)) return false;
    return previewProfile.value[profileKeyForSection(section)] !== undefined;
  };

  const hasAnyUnresolved = computed(() => (
    Object.keys(previewProfile.value).length > 0 || !!avatarPreview.value
  ));

  const setPreview = (extracted: Partial<Profile>) => {
    previewProfile.value = { ...extracted };
  };

  const setPreviewSection = (
    section: PreviewableSection,
    value: Partial<Profile>[ReturnType<typeof profileKeyForSection>] | undefined,
  ) => {
    const profileKey = profileKeyForSection(section);
    const nextPreview = { ...previewProfile.value };

    if (value === undefined) {
      delete nextPreview[profileKey];
    } else {
      (nextPreview as Record<string, unknown>)[profileKey] = value;
    }

    previewProfile.value = nextPreview;
  };

  const discardProfileSection = (section: ProfileSectionKey) => {
    if (!isPreviewableSection(section)) return;
    const profileKey = profileKeyForSection(section);
    if (previewProfile.value[profileKey] === undefined) return;
    const next = { ...previewProfile.value };
    delete next[profileKey];
    previewProfile.value = next;
  };

  const discardSection = (section: ProfileSectionKey) => {
    if (section === 'basic') avatarPreview.value = null;
    discardProfileSection(section);
  };

  const discardAll = () => {
    previewProfile.value = {};
    avatarPreview.value = null;
  };

  const setAvatarPreview = (preview: AvatarPreview | null) => {
    avatarPreview.value = preview;
  };

  const discardAvatarPreview = () => {
    avatarPreview.value = null;
  };

  return {
    previewProfile,
    avatarPreview,
    hasUnresolvedFor,
    hasAnyUnresolved,
    setPreview,
    setPreviewSection,
    setAvatarPreview,
    discardProfileSection,
    discardSection,
    discardAvatarPreview,
    discardAll,
  };
});

export default useProfilePreview;
